<!-- src/components/Financeiro/Receitas/EditarReceitasGerais.vue -->
<template>
  <div class="modal-overlay">
    <div class="modal-container">
      <!-- Cabeçalho -->
      <div class="modal-header">
        <h6>Editar Receita</h6>
        <i class="bi bi-x-circle close-icon" @click="cancel"></i>
      </div>
      <div class="spacer"></div>

      <!-- Dados do Registro (readonly) -->
      <div class="modal-content">
        <p v-if="loading" role="status">Carregando receita...</p>
        <p v-if="error" class="alert alert-danger" role="alert">{{ error }}</p>
        <fieldset :disabled="loading || !loaded || saving" class="border-0 p-0 m-0">
        <!-- Badge de Tipo -->
        <h5 class="section-title">
          <span v-if="/aula avulsa/i.test(registro.tipo)" class="text-primary">Aula Avulsa</span>
          <span v-else-if="/aula experimental/i.test(registro.tipo)" class="text-warning">Aula Experimental</span>
          <span v-else-if="/vendas da loja/i.test(registro.tipo)" class="text-success">Vendas da Loja</span>
          <span v-else>{{ registro.tipo }}</span>
        </h5>
        <!-- Informações fixas -->
        <div class="row mb-3">
          <div class="col-md-6">
            <div class="form-group">
              <label>Descrição:</label>
              <input
                type="text"
                :value="registro.descricao"
                class="form-control"
                disabled
              />
            </div>
          </div>
          <div class="col-md-3">
            <div class="form-group">
              <label>Vencimento:</label>
              <input
                type="date"
                :value="registro.dataVencimento"
                class="form-control"
                disabled
              />
            </div>
          </div>
          <div class="col-md-3">
            <div class="form-group">
              <label>Valor:</label>
              <input
                type="text"
                :value="formatBRL(registro.valor)"
                class="form-control"
                @input="onValorInput"
              />
            </div>
          </div>
        </div>

        <!-- Seção de edição -->
        <h5 class="section-title">Editar Dados</h5>
        <div class="row mb-3">
          <!-- Forma de Pagamento -->
          <div class="col-md-4">
            <div class="form-group">
              <label>Forma de Pagamento:</label>
              <select
                v-model.number="formData.forma_pagamento_id"
                class="form-control"
              >
                <option value="">Selecione</option>
                <option
                  v-for="f in formas"
                  :key="f.id"
                  :value="f.id"
                >{{ f.nome }}</option>
              </select>
            </div>
          </div>

          <!-- Campos condicionais de Cheque -->
          <div
            v-if="canReadFinancial && isCheque(formData.forma_pagamento_id)"
            class="col-md-2"
          >
            <div class="form-group">
              <label>Banco nº:</label>
              <input
                type="text"
                v-model="formData.banco_numero"
                class="form-control"
              />
            </div>
          </div>
          <div
            v-if="canReadFinancial && isCheque(formData.forma_pagamento_id)"
            class="col-md-2"
          >
            <div class="form-group">
              <label>Agência:</label>
              <input
                type="text"
                v-model="formData.agencia"
                class="form-control"
              />
            </div>
          </div>
          <div
            v-if="isCheque(formData.forma_pagamento_id)"
            class="col-md-2"
          >
            <div class="form-group">
              <label>Cheque nº:</label>
              <input
                type="text"
                v-model="formData.cheque_numero"
                class="form-control"
              />
            </div>
          </div>

          <!-- Data de Pagamento -->
          <div class="col-md-2">
            <div class="form-group">
              <label>
                Data de Pagamento
                <span v-if="!isAInformar(formData.forma_pagamento_id)" class="required">*</span>
              </label>
              <input
                type="date"
                v-model="formData.data_pagamento"
                :disabled="isAInformar(formData.forma_pagamento_id)"
                class="form-control"
              />
            </div>
          </div>

          <!-- Data de Recebimento -->
          <div class="col-md-2">
            <div class="form-group">
              <label>Recebimento:</label>
              <input
                type="date"
                v-model="formData.data_recebimento"
                :disabled="!canEditRecebimento || isAInformar(formData.forma_pagamento_id)"
                class="form-control"
              />
            </div>
          </div>

          <!-- Conta Financeira -->
          <div class="col-md-4" v-if="canReadFinancial || canSelectOperationalAccount">
            <div v-if="contasMensagem" class="alert alert-warning" role="alert">{{ contasMensagem }}</div>
            <div class="form-group">
              <label>
                Conta Financeira
                <span
                  v-if="!isAInformar(formData.forma_pagamento_id) && !!(formData.data_pagamento && formData.data_pagamento.trim())"
                  class="required"
                >*</span>
              </label>
              <select
                v-model.number="formData.conta_financeira_id"
                class="form-control"
                :disabled="isAInformar(formData.forma_pagamento_id)"
                :required="!isAInformar(formData.forma_pagamento_id) && !!(formData.data_pagamento && formData.data_pagamento.trim())"
              >
                <!-- A informar -->
                <option v-if="isAInformar(formData.forma_pagamento_id)" :value="null">A informar</option>

                <!-- Normal (inclui Espécie) -->
                <template v-else>
                  <option :value="null" disabled>Selecione</option>
                  <option v-for="c in contasFinanceiras" :key="c.id" :value="c.id">
                    {{ c.texto }}
                  </option>
                </template>
              </select>
            </div>
          </div>

          <!-- Autorização (Cartão) -->
          <div class="col-md-4" v-if="canReadFinancial && isCartao(formData.forma_pagamento_id)">
            <div class="form-group">
              <label>Autorização <span class="required">*</span></label>
              <input
                type="text"
                v-model="formData.numero_autorizacao"
                class="form-control"
                placeholder="Número da autorização"
                :disabled="isAInformar(formData.forma_pagamento_id)"
              />
            </div>
          </div>

          <!-- Observações -->
          <div class="col-md-12" v-if="canReadFinancial">
            <div class="form-group">
              <label>Observações:</label>
              <textarea
                v-model="formData.observacoes"
                class="form-control"
                rows="3"
              ></textarea>
            </div>
          </div>
        </div>

        </fieldset>

        <!-- Ações -->
        <div class="d-flex justify-content-end mt-4 mb-4">
          <button class="btn btn-secondary me-2" @click="cancel">Cancelar</button>
          <button class="btn btn-primary" :disabled="loading || !loaded || saving || !formValid" v-if="hasPermission('alterar_receitas_gerais')" @click="save">
            Salvar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useReceitasPermissions } from '@/composables/useReceitasPermissions';
import { ref, reactive, computed, onMounted, watch, nextTick, getCurrentInstance } from 'vue';
import axios from '@/services/axios';
import { loadOperationalAccounts, accountSelectionError, emptyAccountsMessage } from '@/services/operationalAccounts';
import globalUtils from '@/utils/globalUtils.js';

export default {
  name: 'EditarReceitasGerais',
  props: {
    id: { type: Number, required: true },
    origem: { type: String, required: true }   // ⬅ NOVO
  },
  setup(props, { emit }) {
    const { fetchPermissions, hasPermission, authorizeReceiptAction } = useReceitasPermissions();

    const instance = getCurrentInstance();
    const gp = instance?.appContext.config.globalProperties;

    const canEditRecebimento = computed(() => {
      // 1) via lista de permissões
      const perms = gp?.$globalState?.userPermissions;
      const hasAdminPerm = Array.isArray(perms)
        ? perms.map(s => String(s).toLowerCase().trim()).includes('administrador')
        : false;

      // 2) via perfil/role (string ou objeto)
      const rawProfile = gp?.$globalState?.userProfile;
      const profileStr = typeof rawProfile === 'string'
        ? rawProfile
        : (rawProfile?.perfil ?? rawProfile?.role ?? gp?.$globalState?.role ?? '');
      const isAdminByTitle = /(^|\s)(administrador|admin)(\s|$)/i.test(String(profileStr).trim());

      // 3) flag booleana opcional
      const isAdminFlag = !!gp?.$globalState?.isAdmin;

      return hasAdminPerm || isAdminByTitle || isAdminFlag;
    });

    const hydrating = ref(true);
    const loading = ref(true);
    const loaded = ref(false);
    const saving = ref(false);
    const error = ref('');
    const canReadFinancial = computed(() => hasPermission('detalhar_contas_financeiras'));
    let originalPayload = {};

    // registro fixo (readonly)
    const registro = reactive({
      tipo: '',
      descricao: '',
      dataVencimento: '',
      valor: 0
    });

    // dropdowns e form data editável
    const formas = ref([]);
    const contasFinanceiras = ref([]);
    const contasMensagem = ref('');
    const originallyReceived = ref(false);
    const canSelectOperationalAccount = computed(() => hasPermission('alterar_receitas_gerais') &&
      ['receita_geral','aula_avulsa','aula_experimental'].includes(props.origem) && !originallyReceived.value);
    const formData = reactive({
      forma_pagamento_id: null,
      data_pagamento: '',
      data_recebimento:  '',
      conta_financeira_id: null,
      observacoes: '',
      banco_numero: '',
      agencia: '',
      cheque_numero: '',
      numero_autorizacao: null,
    });

    const norm = s => (s ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g,'').trim().toLowerCase();

    const contaPadraoGlobalId = computed(() => {
      const c = (contasFinanceiras.value || []).find(x => x.conta_padrao);
      return c?.id ?? null;
    });

    const getFormaById = (id) => (formas.value || []).find(f => Number(f.id) === Number(id));

    const isCartao = (formaId) => norm(getFormaById(formaId)?.nome).includes('cartao');

    // por forma -> fallback global (exceto espécie/a informar)
    const aplicarContaPadraoSeVazio = (formaId) => {
      const forma = getFormaById(formaId);
      if (!forma) return;

      const nome = norm(forma.nome);
      if (nome === 'especie' || nome === 'a informar' || nome === 'informar') return;

      if (formData.conta_financeira_id != null && formData.conta_financeira_id !== '') return;

      const porForma = forma.conta_financeira_padrao_id ? Number(forma.conta_financeira_padrao_id) : null;
      if (porForma) { formData.conta_financeira_id = porForma; return; }

      if (contaPadraoGlobalId.value) formData.conta_financeira_id = contaPadraoGlobalId.value;
    };


    // 👇 adicione junto aos outros helpers/computed
    const idBrinde = computed(() =>
      (formas.value || []).find(f => norm(f.nome) === 'brinde')?.id ?? null
    );

    // guarda o valor original para poder restaurar quando sair de Brinde
    const valorOriginal = ref(0);

    // identifica “A informar”
    const idAInformar = computed(() =>
      (formas.value || []).find(f => norm(f.nome).includes('informar'))?.id ?? null
    );
    function isAInformar(formaId) {
      return formaId === idAInformar.value;
    }

    // ID da forma “Espécie”
    const idEspecie = computed(() =>
      formas.value.find(f => f.nome.toLowerCase() === 'espécie')?.id || null
    );
    function isEspecie(formaId) {
      return formaId === idEspecie.value;
    }

    const formatBRL = (v) =>
        (Number(v) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

    function onValorInput(e) {
      // Mantém só dígitos e converte para centavos → número
      const digits = e.target.value.replace(/\D/g, '');
      const valor = digits ? Number(digits) / 100 : 0;

      // Atualiza o model numérico
      registro.valor = valor;

      // Se NÃO for Brinde, atualiza o “valor original”
      if (formData.forma_pagamento_id !== idBrinde.value) {
        valorOriginal.value = valor;
      }

      // Reaplica a máscara no input
      e.target.value = formatBRL(valor);
    }

    // Carrega contas financeiras
    async function loadContas() {
      contasFinanceiras.value = [];
      contasMensagem.value = '';
      try {
        contasFinanceiras.value = await loadOperationalAccounts('receita_geral_recebimento');
        if (!contasFinanceiras.value.length) contasMensagem.value = emptyAccountsMessage;
      } catch (error) { contasMensagem.value = accountSelectionError(error); }
    }


    // form validation
    const formValid = computed(() => {
      if (!loaded.value || loading.value || saving.value) return false;
      if (!canReadFinancial.value) {
        if (canSelectOperationalAccount.value && formData.data_pagamento && !isAInformar(formData.forma_pagamento_id) && !formData.conta_financeira_id) return false;
        return !!formData.forma_pagamento_id && Number.isFinite(Number(registro.valor)) && Number(registro.valor) >= 0;
      }
      // 1) precisa de forma
      if (!formData.forma_pagamento_id) return false;

      // 2) Data de pagamento NÃO é obrigatória (exceto se você quiser manter)
      //    Se quiser manter obrigatória quando não for "A informar", descomente:
      if (!isAInformar(formData.forma_pagamento_id) && !formData.data_pagamento) return false;

      // 3) Conta financeira: somente se houver data_pagamento (inclui Espécie)
      if (
        !isAInformar(formData.forma_pagamento_id) &&
        (formData.data_pagamento || '').trim() &&
        !formData.conta_financeira_id
      ) {
        return false;
      }

      // 4) Cheque: se informou pagamento, exige dados do cheque
      if (
        isCheque(formData.forma_pagamento_id) &&
        (formData.data_pagamento || '').trim()
      ) {
        return !!(formData.banco_numero && formData.agencia && formData.cheque_numero);
      }

      // Cartão exige autorização
      if (isCartao(formData.forma_pagamento_id)) {
        if (!(formData.numero_autorizacao || '').trim()) return false;
      }

      return true;
    });

    // helpers
    const formatCurrency = v =>
      Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

    const isCheque = id => {
      const f = formas.value.find(x => x.id === id);
      return f ? f.nome.toLowerCase() === 'cheque' : false;
    };

    // load registro e formData
    const loadData = async () => {
      const res = await axios.get(`/financeiro/receitas-completas/${props.origem}/${props.id}`);
      const r = res.data.data;
      originallyReceived.value = !!(r.data_pagamento || r.data_recebimento || r.pago);

      registro.tipo           = r.tipo;
      registro.descricao      = r.descricao;
      registro.dataVencimento = (r.data_vencimento || '').split('T')[0] || r.data_vencimento || '';
      // ✅ Valor exibido no modal
      // - aula_experimental: mostra valor já com desconto (valor_liquido) se o backend enviar
      // - demais: mantém valor normal
      const valorExibido =
        (String(props.origem).toLowerCase() === 'aula_experimental')
          ? (Number(r.valor_liquido ?? r.valor) || 0)   // usa valor_liquido se existir
          : (Number(r.valor) || 0);

      registro.valor = valorExibido;

      // ✅ valorOriginal precisa seguir o que está exibido
      valorOriginal.value = valorExibido;

      formData.forma_pagamento_id  = r.forma_pagamento_id != null ? Number(r.forma_pagamento_id) : null;
      formData.data_pagamento     = r.data_pagamento?.split('T')[0] || '';
      formData.data_recebimento = r.data_recebimento?.split('T')[0] || '';
      formData.conta_financeira_id = r.conta_financeira_id != null ? Number(r.conta_financeira_id) : null;
      formData.numero_autorizacao  = r.numero_autorizacao || null;
      formData.observacoes         = r.observacoes || '';
      formData.banco_numero        = r.banco_numero || '';
      formData.agencia             = r.agencia || '';
      formData.cheque_numero       = r.cheque_numero || '';
    };

    const loadFormas = async () => {
      const res = await axios.get('/cadastros/formas-pagamento');
      const lista = (res.data.data || res.data || []);

      const norm = s => (s ?? '')
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .trim().toLowerCase();

      const ordemPersonalizada = {
        'pix': 1,
        'especie': 2,
        'cartao de credito a vista': 3,
        'cartao de credito parcelado': 4,
        'cartao de debito': 5,
        'boleto': 6,
        'transferencia bancaria': 7,
        'cheque': 8,
        'a informar': 9,
      };

      formas.value = Array.isArray(lista)
        ? lista
            .map(f => ({
              ...f,
              id: Number(f.id),
              conta_financeira_padrao_id: f.conta_financeira_padrao_id != null
                ? Number(f.conta_financeira_padrao_id)
                : null
            }))
            .sort((a, b) => {
              const oa = ordemPersonalizada[norm(a.nome)] ?? 999;
              const ob = ordemPersonalizada[norm(b.nome)] ?? 999;

              if (oa !== ob) return oa - ob;
              return a.nome.localeCompare(b.nome, 'pt-BR');
            })
        : [];
    };

    const buildPayload = () => {
      const toNullIfEmpty = v => (typeof v === 'string' && v.trim() === '' ? null : v);
      // monta payload
      const payload = {
        // ⬇️ agora enviamos o valor (0 se for Brinde)
        valor: formData.forma_pagamento_id === idBrinde.value ? 0 : Number(registro.valor) || 0,

        forma_pagamento_id: formData.forma_pagamento_id,
        data_pagamento:     isAInformar(formData.forma_pagamento_id) ? null : toNullIfEmpty(formData.data_pagamento),
        data_recebimento:   isAInformar(formData.forma_pagamento_id) ? null : toNullIfEmpty(formData.data_recebimento),
        conta_financeira_id: formData.conta_financeira_id || null,
        observacoes:        formData.observacoes,
        banco_numero:       formData.banco_numero || null,
        agencia:            formData.agencia || null,
        cheque_numero:      formData.cheque_numero || null,
        numero_autorizacao: isCartao(formData.forma_pagamento_id)
          ? ((formData.numero_autorizacao || '').trim() || null)
          : null,
      };

      if (!canReadFinancial.value) {
        for (const key of ['observacoes', 'banco_numero', 'agencia', 'numero_autorizacao']) delete payload[key];
        if (!canSelectOperationalAccount.value || !payload.data_pagamento || !payload.conta_financeira_id) delete payload.conta_financeira_id;
      }
      return payload;
    };

    const save = async () => {
      if (loading.value || !loaded.value || saving.value) return;
      if (!await authorizeReceiptAction('alterar_receitas_gerais')) {
        error.value = 'Você não possui permissão para alterar esta receita.';
        return;
      }
      if (!formValid.value) return;
      const payload = Object.fromEntries(Object.entries(buildPayload())
        .filter(([key, value]) => value !== originalPayload[key]));
      saving.value = true;
      error.value = '';
      try {
        await axios.put(`/financeiro/receitas-completas/${props.origem}/${props.id}`, payload);
        emit('updated');
      } catch (err) {
        error.value = err.response?.data?.error || 'Não foi possível salvar a receita. Tente novamente.';
      } finally {
        saving.value = false;
      }
    };

    const cancel = () => emit('cancel');

    watch(
      () => formData.forma_pagamento_id,
      (novaForma, antigaForma) => {
        if (hydrating.value) return;

        // ao trocar forma: zera conta e autorização
        formData.conta_financeira_id = null;
        formData.numero_autorizacao = null;

        // se saiu de cheque, limpa campos
        if (antigaForma && !isCheque(novaForma)) {
          formData.banco_numero = '';
          formData.agencia = '';
          formData.cheque_numero = '';
        }

        // A informar: zera datas e conta
        if (isAInformar(novaForma)) {
          formData.data_pagamento = '';
          formData.data_recebimento = '';
          formData.conta_financeira_id = null;
          return;
        }

        // Espécie: não auto-preenche conta
        if (isEspecie(novaForma)) {
          // mantém datas coerentes (se quiser)
          if (!formData.data_pagamento) formData.data_pagamento = registro.dataVencimento || '';
          return;
        }

        // demais: se tiver data_pagamento, tenta conta padrão
        aplicarContaPadraoSeVazio(novaForma);
      },
      { immediate: false }
    );


    // 👇 complemente o watch já existente de [data_pagamento, forma_pagamento_id]
    watch(
      [() => formData.data_pagamento, () => formData.forma_pagamento_id],
      ([dataPag, formaId]) => {
        if (hydrating.value) return;

        // BRINDE: preserva sua regra de valor
        if (formaId === idBrinde.value) {
          registro.valor = 0;
        } else {
          registro.valor = valorOriginal.value;
        }

        // A INFORMAR → limpa
        if (isAInformar(formaId)) {
          formData.data_pagamento      = '';
          formData.data_recebimento    = '';
          formData.conta_financeira_id = null;
          return;
        }

        // Demais: se há data e forma, delega ao globalUtils
        formData.data_recebimento = (dataPag && formaId)
          ? (globalUtils.calcularDataRecebimento(dataPag, formaId, formas.value) || '')
          : '';
      },
      { immediate: true }
    );

    watch(
      () => registro.valor,
      (novo) => {
        // só atualiza o “original” se NÃO estiver em Brinde
        if (formData.forma_pagamento_id !== idBrinde.value) {
          valorOriginal.value = Number(novo) || 0;
        }
      }
    );

    onMounted(async () => {
      try {
        await fetchPermissions();
        if (!hasPermission('alterar_receitas_gerais')) throw new Error('Você não possui permissão para alterar esta receita.');
        await loadFormas();
        await loadContas();
        await loadData();
        await nextTick();
        originalPayload = buildPayload();
        loaded.value = true;
        hydrating.value = false;
      } catch (err) {
        error.value = err.response?.data?.error || err.message || 'Não foi possível carregar a receita.';
      } finally {
        loading.value = false;
      }
    });

    return { hasPermission, loading, loaded, saving, error, canReadFinancial, canSelectOperationalAccount, contasMensagem,
      registro,
      formas,
      contasFinanceiras,
      formData,
      formValid,
      formatCurrency,
      isCheque,
      isAInformar,
      isEspecie,
      save,
      cancel,
      formatBRL,
      onValorInput,
      canEditRecebimento,
      isCartao,
    };
  }
};
</script>


<style scoped>
/* Ajusta o cabeçalho do modal */
  /* Cabeçalho do modal */
  .modal-header {
    background: #ccc; /* cor cinza */
    padding: 10px 15px; 
    display: flex;
    justify-content: space-between;
    align-items: center;
  }


  /* Estilos gerais do modal */
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;

  }

  .modal-container {
  background: #fff;
  width: 100%;
  max-width: 60%; /* ajuste conforme necessário */
  height: auto; /* define a altura fixa */
  overflow-y: auto; /* adiciona barra de rolagem vertical se necessário */
  margin: 0 10px;
  border-radius: 0px;

  /* Remova margens duplicadas se estiverem interferindo */
}

/* Conteúdo do modal */
.modal-content {
padding: 15px;

}


.modal {
  display: block;
  background: white;
  width: 60%;
  max-width: 90%;
  max-height: 80%;
  overflow-y: auto;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

  /* Ícone de fechar */
  .close-icon {
    font-size: 1.5rem;
    cursor: pointer;
  }

/* Espaço semelhante ao utilizado no Formulário */
.spacer {
  margin-bottom: 20px;
}

/* Seções e grupos (estilo do formulário) */
.form-section {
  background: white;
  padding: 15px;
  border-radius: 0px;
  border: 1px solid #dee2e6;
  margin-bottom: 1rem;
}

.section-title {
  font-size: 0.9rem;
  font-weight: bold;
  margin-bottom: 10px;
}

.form-group {
  margin-bottom: 10px;
}

.form-group label {
  font-weight: bold;
  margin-bottom: 5px;
  display: block;
}

.required {
  color: red;
}


/* Inputs e selects */
.form-control {
  width: 100%;
  padding: 5px;
  border: 1px solid #ced4da;
  border-radius: 0px;
}

/* Ajustes para botões de ação para turmas e pagamentos */
.turma-item,
.table .btn {
  margin-top: 0px;
}

.add-turma-container {
  display: flex;
  justify-content: flex-end; /* Alinha o botão à direita */
  margin-top: 5px; /* Pequeno espaçamento acima */
}

/* Esconde o input nativo */
.toggle-input {
  opacity: 0;
  width: 0;
  height: 0;
}


/* Esconde o input nativo */
.toggle-input {
  opacity: 0;
  width: 0;
  height: 0;
}

.spacer {
  margin-bottom: 20px;
}

.btn-warning {
  font-size: 12px;
}
</style>
